import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import {
  NOTATION_PANEL_MAX_HEIGHT,
  NOTATION_PANEL_MIN_HEIGHT,
  clampNotationPanelHeight,
  useUIStore,
} from '@/stores/uiStore';

interface UseNotationPanelResizeArgs {
  /**
   * Layout-aware upper bound: the largest notation height that still
   * leaves the waterfall its 300px fallback floor above the compact
   * keyboard and controls bar. Pass null when the container is unmeasured
   * (first paint, jsdom) — the static [120, 500] clamp always applies.
   */
  maxFromLayout: number | null;
}

interface UseNotationPanelResizeResult {
  /** Height (px) to render the panel at right now — live during a drag. */
  notationHeight: number;
  /** Attach to the drag handle's onMouseDown. */
  onHandleMouseDown: (e: ReactMouseEvent<HTMLDivElement>) => void;
  /** True while a drag is active (drives cursor/handle styling). */
  isResizing: boolean;
}

/**
 * OTO-28 drag-to-resize for the notation panel. The handle sits on the
 * panel's TOP border, so moving the pointer UP (smaller clientY) grows
 * the panel. Height derives from the drag-start snapshot (not
 * incremental deltas) so the panel tracks the pointer exactly, even
 * after leaving and re-entering the clamp range. The store is written
 * once per gesture (mouseup or window blur): zustand persist writes localStorage
 * synchronously on every set, and a write per mousemove would jank the
 * drag.
 */
export function useNotationPanelResize({
  maxFromLayout,
}: UseNotationPanelResizeArgs): UseNotationPanelResizeResult {
  const storedHeight = useUIStore((s) => s.notationPanelHeight);
  const setNotationPanelHeight = useUIStore((s) => s.setNotationPanelHeight);

  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const dragStartRef = useRef<{ startY: number; startHeight: number } | null>(null);
  const latestHeightRef = useRef(clampNotationPanelHeight(storedHeight));

  const isResizing = dragHeight !== null;
  const notationHeight = dragHeight ?? clampNotationPanelHeight(storedHeight);

  const dragMax =
    maxFromLayout === null
      ? NOTATION_PANEL_MAX_HEIGHT
      : Math.max(NOTATION_PANEL_MIN_HEIGHT, Math.min(NOTATION_PANEL_MAX_HEIGHT, maxFromLayout));

  const onHandleMouseDown = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // F2b: left button only -- right/middle click must not start a drag
    e.preventDefault(); // suppress text selection from the drag gesture
    const startHeight = clampNotationPanelHeight(useUIStore.getState().notationPanelHeight);
    dragStartRef.current = { startY: e.clientY, startHeight };
    latestHeightRef.current = startHeight;
    setDragHeight(startHeight);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const onMouseMove = (e: MouseEvent) => {
      const start = dragStartRef.current;
      if (!start) return;
      const proposed = start.startHeight + (start.startY - e.clientY);
      const next = Math.max(NOTATION_PANEL_MIN_HEIGHT, Math.min(dragMax, Math.round(proposed)));
      latestHeightRef.current = next;
      setDragHeight(next);
    };

    // F2b: single termination path shared by mouseup AND window blur --
    // losing focus mid-drag (alt-tab, devtools) would otherwise leave the
    // cursor/user-select/drag state stuck until a later unrelated mouseup.
    const endDrag = () => {
      dragStartRef.current = null;
      setNotationPanelHeight(latestHeightRef.current); // one persisted write per gesture
      setDragHeight(null);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('blur', endDrag);
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', endDrag);
      window.removeEventListener('blur', endDrag);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isResizing, dragMax, setNotationPanelHeight]);

  return { notationHeight, onHandleMouseDown, isResizing };
}
